/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { Checkbox } from '@fluentui/react/lib/Checkbox';
import { Stack } from '@fluentui/react/lib/Stack';
import { observer } from 'mobx-react';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { BasicModal, BasicModalProps } from 'src/core/components/BasicModal';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { withModalMounter } from 'src/core/utils/hocs/withModalMounter';
import { tokenToCharIndices } from 'src/core/utils/labelingUtils';
import { UtteranceObservable } from 'src/luis/appShell/buildPane/intentPage/types';
import { Pattern } from 'src/luis/modules/patterns/Pattern';
import { Entity } from 'src/luis/projects/entities/types/Entity';
import { isMachineLearned } from 'src/luis/projects/entities/utils/entityHelpers';
import { EntityLabel, Utterance } from 'src/luis/projects/utterances/types/Utterance';
import { resolveOverlappingEntities } from 'src/luis/projects/utterances/utils/segmentExtraction';
import { ModelType } from 'src/luis/types/ModelType';
import { PatternInput } from 'src/luis/verticals/patternInput/PatternInput';

export type AddAsPatternModalProps = BasicModalProps<AddAsPatternModalDto> & {
    /**
     * The Utterance object to extract the patterns from.
     */
    selectedUtteranceObservable: UtteranceObservable;

    /**
     * The entities list.
     */
    entities: Entity[];
};

/**
 * @param pattern The pattern value to be displayed.
 * @param isValid The state which indicates if this is a valid pattern or not.
 * @param isSelected The Checkbox state .
 */
type RenderablePattern = {
    pattern: Pattern;
    isValid: boolean;
    isSelected: boolean;
};

export type AddAsPatternModalDto = {
    /**
     * The values of the patterns fields.
     */
    patterns: Pattern[];
};

const messages = defineMessages({
    modalTitle: {
        id: 'utterances.addAsPatternModal.modalTitle',
        defaultMessage: 'Confirm patterns'
    }
});

/**
 * Represents a modal to add new patterns.
 * @param props The properties passed to the modal.
 */
const AddAsPatternModalBase = withModalMounter(
    observer((props: AddAsPatternModalProps) => {
        const { entities, selectedUtteranceObservable, onSubmit } = props;
        const { localize } = useLocalization();

        const [utterance] = selectedUtteranceObservable.computed.get();
        const [suggestedPatterns] = React.useState(() => getSuggestedPatterns(utterance));
        const [patternItems, setPatternItems] = React.useState<RenderablePattern[]>(
            suggestedPatterns.map(p => ({ pattern: p, isValid: true, isSelected: true }))
        );
        /**
         * Filters the data from the form dto
         * and passes only patterns which are selected
         * to the submit callback received from the consumer.
         */
        const onSubmitPatterns = () => {
            const selectedPatterns = patternItems.filter(item => item.isSelected).map(item => item.pattern);
            onSubmit({ patterns: selectedPatterns });
        };

        /**
         * Updates form valid state by iterating over RenderablePattern list and
         * validating that all selected PatternInputs are valid
         * and at least one PatternInput is selected.
         */
        const arePatternItemsValid = () => {
            const atLeastOneCheckedValid = patternItems.some(item => item.isValid && item.isSelected);
            const allCheckedAreValid = patternItems.every(item => !item.isSelected || (item.isSelected && item.isValid));

            return atLeastOneCheckedValid && allCheckedAreValid;
        };

        const isModalValid = arePatternItemsValid();

        /**
         * Updates RenderablePattern list based on selection or text field change events
         */
        const updatePatternItems = (index: number, dto: Partial<RenderablePattern>) => {
            const updatedItems = patternItems.map((item, i) => (i === index ? { ...item, ...dto } : item));
            setPatternItems(updatedItems);
        };

        /**
         * return the list of PatternInput and Checkbox for every suggested
         * pattern returned.
         */
        const items = patternItems.map((item, index: number) => (
                <Stack horizontal verticalAlign="center" key={index}>
                    <Checkbox checked={item.isSelected} onChange={(__, isSelected) => updatePatternItems(index, { isSelected })} />
                    <Stack.Item grow>
                        <PatternInput
                            entities={entities}
                            initialPattern={item.pattern.text}
                            onPatternChange={(text, isValid) => updatePatternItems(index, { isValid, pattern: { ...item.pattern, text } })}
                        />
                    </Stack.Item>
                </Stack>
            ));

        return (
            <BasicModal
                {...props}
                valid={isModalValid}
                onSubmit={onSubmitPatterns}
                dialogContentProps={{ title: localize(messages.modalTitle) }}>
                <Stack tokens={{ childrenGap: 20 }}>{items}</Stack>
            </BasicModal>
        );
    })
);

/**
 * Sorts entities by their length.
 */
const sortByLength = (a: EntityLabel, b: EntityLabel): 1 | -1 => (a.endTokenIndex - a.startTokenIndex < b.endTokenIndex - b.startTokenIndex ? 1 : -1);

/**
 * Generates suggested pattern for given utterance entities.
 *
 * @param entities Utterance entities sorted despondingly by startTokenIndex.
 * @returns The suggested pattern text.
 */
const generateSuggestedPattern = (entities: EntityLabel[], utterance: Utterance): string => {
    let pattern: string = utterance.text;

    entities.forEach(item => {
        const { startCharIndex, endCharIndex } = tokenToCharIndices({
            text: pattern,
            tokenizedText: utterance.tokenizedText,
            startTokenIndex: item.startTokenIndex,
            endTokenIndex: item.endTokenIndex
        });
        pattern = `${pattern.slice(0, startCharIndex)}{${item.entity.name}}${pattern.slice(endCharIndex + 1)}`;
    });

    return pattern;
};

/**
 * Checks if entity is not wrapped in composite entity.
 *
 * @param entity The entity to check.
 * @param compositeEntities The composite entities.
 * @returns True if the entity is not wrapped in a composite entity, false otherwise.
 */
const isEntityNotInComposite = (entity: EntityLabel, compositeEntities: EntityLabel[]): boolean => {
    for (const cE of compositeEntities) {
        if (entity.startTokenIndex >= cE.startTokenIndex && entity.startTokenIndex <= cE.endTokenIndex) {
            return false;
        }
    }

    return true;
};

const getSuggestedPatterns = (utterance: Utterance): Pattern[] => {
    const patterns: Pattern[] = [];
    const resolvedEntities: EntityLabel[] = resolveOverlappingEntities(
        utterance.labeledEntities,
        utterance.predictedEntities.filter(({ entity: { typeId } }) => !isMachineLearned(typeId))
    );

    const nonCompositeEntities: EntityLabel[] = resolvedEntities
        .filter(({ entity: { typeId } }) => typeId !== ModelType.Composite)
        .sort(sortByLength);

    if (nonCompositeEntities.length) {
        const childrenEntitiesPattern: string = generateSuggestedPattern(nonCompositeEntities, utterance);
        patterns.push({
            id: '',
            text: childrenEntitiesPattern,
            labeledIntent: utterance.labeledIntent,
            usedEntitiesAndRoles: []
        });
    }

    const compositeEntities: EntityLabel[] = resolvedEntities.filter(({ entity: { typeId } }) => typeId === ModelType.Composite);
    if (compositeEntities.length) {
        const entitiesNotInComposites: EntityLabel[] = nonCompositeEntities.filter(nC => isEntityNotInComposite(nC, compositeEntities));
        const compositePatternEntities: EntityLabel[] = [...compositeEntities, ...entitiesNotInComposites].sort((a, b) => (a.startTokenIndex > b.startTokenIndex ? -1 : 1));
        const compositePattern: string = generateSuggestedPattern(compositePatternEntities, utterance);
        patterns.push({
            id: '',
            text: compositePattern,
            labeledIntent: utterance.labeledIntent,
            usedEntitiesAndRoles: []
        });
    }

    return patterns;
};

export const AddAsPatternModal = (addAsPatternModalProps: AddAsPatternModalProps) => (addAsPatternModalProps.selectedUtteranceObservable ? <AddAsPatternModalBase {...addAsPatternModalProps} /> : null);
